/**
 * Haritanın sabit oyun unsurları: **kuleler** ve **kontrol bölgeleri**.
 *
 * Bunlar bizim ölçümümüz değil — açık şekilde `wardogs.tech`'in savaş odası
 * uygulamasından alındı (aynı harita piramidi, aynı 16.384 m'lik dünya). Oradaki
 * koordinatlar harita görüntüsüne göre **normalize** (0..1, sol üstten) duruyor
 * ve piramit ikimizde de aynı kaynaktan geldiği için doğrudan bizim dünya
 * koordinatımıza çevrilebiliyor:
 *
 *     dünya = normalize × dünyanın kenar uzunluğu
 *
 * Editörde kenar `CANVAS_SIZE` (1080 birim), oyun koordinatında `163.84` birim
 * (= 16.384 m). **Doğrulandı:** üç haritanın "Default" bölge merkezi de
 * `backend/strategies/maps.py` → `GAME_BOUNDS`'un oynanabilir dikdörtgeninin
 * içine, ortalarına denk düşüyor.
 *
 * Yeni harita eklenirken buraya da satır girmeli; yoksa o haritada kule ve
 * bölge katmanı hiç çıkmaz (hata değil, katman yok sayılır).
 */

/** Haritanın kenar uzunluğu — üç haritada da aynı (bkz. TILE_BOUNDS). */
export const MAP_WORLD_M = 16384

export interface MapTower {
  /** Harita görüntüsüne göre normalize konum (0..1, sol üstten). */
  x: number
  y: number
  /** Oyunun kendi adı — **veri**, çevrilmez ve büyütülmez. */
  label: string
}

export interface ControlZone {
  id: string
  /** Oyundaki bölge adı (Default, Farmland, …) — veri. */
  name: string
  /** Bölgenin bağlı olduğu rotasyon; üs/şehir eşlemesi için kaynakta kullanılıyor. */
  rotation: string
  x: number
  y: number
  /** Yarıçap **metre**; dünya birimine `radiusM / MAP_WORLD_M` ile çevrilir. */
  radiusM: number
  /** Bu bölgede oynanan kuleler — `MAP_TOWERS[map]` dizisindeki sıralar. */
  towers: number[]
}

/** Harita slug'ı → kuleler. Sıra önemlidir: bölgeler kuleyi indeksle anar. */
export const MAP_TOWERS: Record<string, MapTower[]> = {
  bakurani: [
    { x: 0.5104, y: 0.5556, label: 'Tower 4' },
    { x: 0.5020, y: 0.5826, label: 'Tower 5 L' },
    { x: 0.4713, y: 0.5516, label: 'Tower 3' },
    { x: 0.4916, y: 0.5735, label: 'Tower 1' },
    { x: 0.4714, y: 0.5729, label: 'Tower 2' },
  ],
  ozeti: [
    { x: 0.6379, y: 0.6113, label: 'Tower 3' },
    { x: 0.6145, y: 0.5872, label: 'Tower 4' },
    { x: 0.5849, y: 0.6163, label: 'Tower 1' },
    { x: 0.6126, y: 0.6384, label: 'Tower 2' },
  ],
  zesty: [
    { x: 0.4283, y: 0.3886, label: 'Tower 3' },
    { x: 0.4187, y: 0.3643, label: 'Tower 1' },
    { x: 0.4449, y: 0.3587, label: 'Tower 2' },
  ],
}

/** Harita slug'ı → kontrol bölgeleri. İlk satır oyunun varsayılan rotasyonudur. */
export const CONTROL_ZONES: Record<string, ControlZone[]> = {
  bakurani: [
    { id: 'bakurani-default',    name: 'Default',    rotation: 'Bakurani', x: 0.4877, y: 0.5617, radiusM: 500, towers: [0, 1, 2, 3, 4] },
    { id: 'bakurani-farmland',   name: 'Farmland',   rotation: 'Bakurani', x: 0.4966, y: 0.5767, radiusM: 500, towers: [0, 1, 3, 4] },
    { id: 'bakurani-lumberyard', name: 'Lumberyard', rotation: 'Bakurani', x: 0.5035, y: 0.5617, radiusM: 500, towers: [0, 1, 3] },
  ],
  ozeti: [
    { id: 'ozeti-default',  name: 'Default',  rotation: 'Madrid', x: 0.6104, y: 0.6123, radiusM: 550, towers: [0, 1, 2, 3] },
    { id: 'ozeti-farmland', name: 'Farmland', rotation: 'Madrid', x: 0.5779, y: 0.6122, radiusM: 550, towers: [2] },
    { id: 'ozeti-church',   name: 'Church',   rotation: 'Madrid', x: 0.6203, y: 0.6142, radiusM: 550, towers: [0, 1, 3] },
    { id: 'ozeti-river',    name: 'River',    rotation: 'Madrid', x: 0.5965, y: 0.6192, radiusM: 550, towers: [2, 3] },
  ],
  zesty: [
    { id: 'zestafona-default',         name: 'Default',         rotation: 'Detroit', x: 0.4312, y: 0.3711, radiusM: 500, towers: [0, 1, 2] },
    { id: 'zestafona-smallfactory',    name: 'Small Factory',   rotation: 'Detroit', x: 0.4344, y: 0.3561, radiusM: 500, towers: [1, 2] },
    { id: 'zestafona-watertreatment',  name: 'Water Treatment', rotation: 'Detroit', x: 0.4272, y: 0.3877, radiusM: 500, towers: [0, 1] },
    { id: 'zestafona-houses',          name: 'Houses',          rotation: 'Detroit', x: 0.4207, y: 0.3651, radiusM: 500, towers: [0, 1, 2] },
  ],
}

/**
 * Tarafların ana üsleri (HQ) — her tarafın doğduğu şehir.
 *
 * Kaynak yine `wardogs.tech` (kuleler ve bölgelerle aynı koordinat sistemi).
 * Bir harita birden çok şehir taşır ama bir rotasyonda yalnızca üçü oynanır:
 * hangi üçünün olduğunu `ROTATION_TOWNS` söyler. Ozeti'de sekiz şehir var,
 * Madrid rotasyonunda Malaga · Barcelona · Granada oynanıyor.
 *
 * `faction` `lib/factions.ts`'in slug'ı — amblem ve renk oradan gelir.
 * Şehir adı oyunun verisidir, çevrilmez ve büyütülmez.
 */
export interface MapBase {
  x: number
  y: number
  faction: 'lonestar' | 'valkyra' | 'manticore'
  town: string
}

export const MAP_BASES: Record<string, MapBase[]> = {
  bakurani: [
    { x: 0.3285, y: 0.6012, faction: 'manticore', town: 'Liknisi' },
    { x: 0.2455, y: 0.5292, faction: 'manticore', town: 'Chuta' },
    { x: 0.7208, y: 0.5710, faction: 'valkyra',   town: 'Khevuli' },
    { x: 0.5293, y: 0.7992, faction: 'lonestar',  town: 'Baghi' },
  ],
  ozeti: [
    { x: 0.5397, y: 0.5415, faction: 'manticore', town: 'Stuttgart' },
    { x: 0.2712, y: 0.6352, faction: 'manticore', town: 'Calais' },
    { x: 0.8391, y: 0.5915, faction: 'valkyra',   town: 'Barcelona' },
    { x: 0.4216, y: 0.4633, faction: 'manticore', town: 'Malaga' },
    { x: 0.5345, y: 0.3786, faction: 'valkyra',   town: 'Rennes' },
    { x: 0.5667, y: 0.7546, faction: 'lonestar',  town: 'Hanover' },
    { x: 0.5113, y: 0.8085, faction: 'lonestar',  town: 'Granada' },
    { x: 0.7410, y: 0.8416, faction: 'valkyra',   town: 'Bremen' },
  ],
  zesty: [
    { x: 0.2407, y: 0.2374, faction: 'valkyra',   town: 'Austin' },
    { x: 0.6388, y: 0.2976, faction: 'manticore', town: 'Chicago' },
    { x: 0.4151, y: 0.5935, faction: 'lonestar',  town: 'Boston' },
  ],
}

/** Rotasyon → o rotasyonda oynanan üç üssün şehri. */
export const ROTATION_TOWNS: Record<string, string[]> = {
  Bakurani: ['Chuta', 'Khevuli', 'Baghi'],
  Madrid:   ['Malaga', 'Barcelona', 'Granada'],
  Detroit:  ['Austin', 'Chicago', 'Boston'],
}

/**
 * Bu haritada oynanan üsler. Rotasyon seçili bölgeden okunur; bölge kapalıyken
 * haritanın varsayılan (ilk) bölgesininkine düşer — bugün her haritanın tek
 * rotasyonu var, dolayısıyla üsler bölge seçilmeden de bellidir.
 */
export function basesOf(mapName: string, zone: ControlZone | null): MapBase[] {
  const rotation = zone?.rotation ?? zonesOf(mapName)[0]?.rotation
  const towns = rotation ? ROTATION_TOWNS[rotation] : undefined
  if (!towns) return []
  return (MAP_BASES[mapName] ?? []).filter(b => towns.includes(b.town))
}

/**
 * Kule simgesi — çizimin kendisi, `<img>` olarak basılır.
 *
 * Dosya **yalnızca içeriğine kırpıldı**; rengi dosyanın kendisidir (beyaz
 * dolgu, siyah kontur) ve katmanın rengiyle zaten aynı. Kırpmanın sebebi: gelen
 * çizim 128²'lik bir tuvalin ortasında 44×67'lik bir alanda duruyordu, yani
 * kutunun üçte biri — "48 px simge" ekranda 25 px çıkıyor ve büyütme isteği hiç
 * karşılık bulmuyordu.
 *
 * ⚠️ **Maske ile basılmaz.** `mask-image` yalnızca alfayı kullanır, yani beyaz
 * dolgu ile siyah konturu tek bir düz silüete indirir — çizimin içi kaybolur,
 * simge şeffaflığını yitirmiş bir leke gibi görünür (denendi). Renk gerektiğinde
 * çözüm maske değil, dosyayı yeniden boyamaktır (parlaklıkla çarparak: beyaz
 * dolgu renge döner, siyah kontur yerinde kalır).
 *
 * Dokunulmamış özgün dosya `tower-source.webp` olarak yanında durur: yeniden
 * boyama ya da farklı bir kırpma gerektiğinde kaynak odur.
 *
 * ⚠️ **Adres sürümlüdür (`?v=`).** Dosyanın adı sabit, içeriği ise değişebiliyor
 * (kırpma, renk); nginx `.webp` için açık bir başlık koymadığından tarayıcı onu
 * sezgisel olarak önbelleğe alıyor ve aynı adreste yeni içerik **görünmüyor** —
 * simge bir renk değişikliğinden sonra günlerce eski hâliyle kaldı. Dosya
 * yeniden üretildiğinde bu sayı da artmalı (tile piramidindeki `TILE_VERSION`
 * ile aynı kural).
 */
export const TOWER_ICON = '/map/tower.webp?v=2'

/**
 * Katmanın rengi — **beyaz**: çember, kenar yıkaması, bölge adı ve kule
 * simgesi hepsi bu.
 *
 * Tek renk kasıtlı: kule ile çember aynı şeyin iki parçası (bölge ve içindeki
 * kuleler), farklı renkler onları iki ayrı katman gibi gösterirdi. Beyaz ayrıca
 * kule dosyasının kendi rengi — dolayısıyla simge boyanmadan, olduğu gibi
 * basılabiliyor (bkz. `TOWER_ICON`).
 */
export const ZONE_COLOR = '#ffffff'

/**
 * Kulenin ekrana basılan adı.
 *
 * Kaynakta bir satır `Tower 5 L` diye geçiyor: sondaki tek harf kaynağın kendi
 * ayırt edici eki (aynı kuleyi iki kez anmamak için), oyunda öyle bir ad yok.
 * Kaynak da onu ekrana basarken aynı şekilde kırpıyor — biz de kırpıyoruz,
 * veriyi olduğu gibi bırakıp.
 */
export function towerLabel(label: string): string {
  return label.replace(/\s+[A-Z]$/, '')
}

export function towersOf(mapName: string): MapTower[] {
  return MAP_TOWERS[mapName] ?? []
}

export function zonesOf(mapName: string): ControlZone[] {
  return CONTROL_ZONES[mapName] ?? []
}

export function findZone(mapName: string, id: string | null): ControlZone | null {
  if (!id) return null
  return zonesOf(mapName).find(z => z.id === id) ?? null
}
